import _noop from 'lodash/noop';
import _isNil from 'lodash/isNil';
import { ENROLLMENT_STEPS_TO_EXPERIMENT_METRICS } from 'src/payments/hooks';
import { DUTCHIE_PAY } from 'src/payments/constants';
import { isBrowser } from 'utils/misc-utils';
import { Session } from 'utils/session';
import { EventTracker } from '../events';
import { createLDClient } from '../clients';
import { AnalyticsOrder } from '..';
import { trackMetricWithFunnelValidation } from '../../funnel-validation';

const getOrderReceiptSummaryValue = ({ receipt }: AnalyticsOrder, key: string): number | undefined => {
  const value = receipt?.summary?.[key];

  if (_isNil(value)) {
    return undefined;
  }

  return value / 100;
};

const trackLDMetricWithFunnelValidation = (client: any, metric: string, value?: number): void => {
  if (!isBrowser()) {
    return;
  }
  const userKey = Session.getInstance().data.id;
  trackMetricWithFunnelValidation(metric, userKey, (m, v) => client.trackMetric(m, v), value);
};

export const createLDTracker = (): EventTracker => {
  const client = createLDClient();

  return {
    onContextChange: ({ ldClient }) => {
      if (ldClient) {
        client.initialize(ldClient);
      }
    },
    cartViewed: () => {
      client.trackMetric('growth.ecomm.analytics.cart-viewed');
    },
    addedProductToCart: () => {
      client.trackMetric('growth.ecomm.analytics.added-product-to-cart');
    },
    productClicked: () => {
      client.trackMetric('growth.ecomm.analytics.product-clicked');
    },
    sponsoredProductClicked: () => {
      client.trackMetric('growth.ecomm.analytics.sponsored-product-clicked');
    },
    placedOrder: (payload) => {
      client.trackMetric('growth.ecomm.analytics.placed-order');

      const totalValue = getOrderReceiptSummaryValue(payload.order, 'total');

      if (totalValue) {
        client.trackMetric('growth.ecomm.analytics.placed-order.total-value', totalValue);
      }
      if (payload.order.paymentMethod === DUTCHIE_PAY) {
        trackLDMetricWithFunnelValidation(client, 'fintech.checkout.pay-by-bank-order-submitted');
      }
    },
    dutchiePayEnrollmentButtonClicked: (payload, context) => {
      // only send events for logged out users coming from the checkout payment section
      if (!context.activeUser?.id && payload.buttonLocation === 'ecomm-checkout-payment-section') {
        trackLDMetricWithFunnelValidation(client, 'fintech.checkout.link-your-bank-clicked');
      }
    },
    dutchiePayEnrollmentStep: (payload) => {
      const metric = ENROLLMENT_STEPS_TO_EXPERIMENT_METRICS[payload.step]?.[payload.action];
      if (metric) {
        trackLDMetricWithFunnelValidation(client, metric);
      }
    },
    // Noop events
    cartUpdated: _noop,
    removedProductFromCart: _noop,
    searchResults: _noop,
    viewedProduct: _noop,
    sponsoredBannerImpression: _noop,
    sponsoredBannerClicked: _noop,
    sponsoredProductImpression: _noop,
    accountLogin: _noop,
    buyItAgainImpression: _noop,
    buyItAgainViewAllClicked: _noop,
    checkoutStarted: _noop,
    viewedCheckout: _noop,
    checkoutSession: _noop,
    searchedForProduct: _noop,
    imageBannerClicked: _noop,
    paymentsError: _noop,
    privacyPreferenceUpdated: _noop,
    dutchiePayCTAButtonClicked: _noop,
    dutchiePayEnrollmentStepCancelled: _noop,
    dutchiePayPlaidOnEvent: _noop,
    dutchiePayPlaidOnLoad: _noop,
    dutchiePayServiceFeeShown: _noop,
    dutchiePayIncentiveExposure: _noop,
    dutchiePayPaymentMethodSelected: _noop,
    dutchiePayPlaidOnSuccess: _noop,
    dutchiePayPlaidOnExit: _noop,
    dutchiePayAeroSyncOnEvent: _noop,
    dutchiePayAeroSyncOnLoad: _noop,
    dutchiePayAeroSyncOnClose: _noop,
    dutchiePayAeroSyncOnSuccess: _noop,
    dutchiePayAeroSyncOnError: _noop,
    dutchiePayPreAuthEvent: _noop,
    dutchiePayInstoreAccountLinkViewed: _noop,
    dutchiePayInstoreLoginViewed: _noop,
    dutchiePayInstoreCartViewed: _noop,
    dutchiePayInstoreCartApprovalClicked: _noop,
    dutchiePayInstoreCartApprovalSucceeded: _noop,
    dutchiePayInstoreCartApprovalFailed: _noop,
    dutchiePayRelinkButtonViewed: _noop,
    dutchiePayRelinkButtonClicked: _noop,
    dutchiePayUnlinkRemoveCtaClicked: _noop,
    dutchiePayUnlinkModalViewed: _noop,
    dutchiePayUnlinkConfirmed: _noop,
    dutchiePayUnlinkCancelled: _noop,
    dutchiePayUnlinkSuccess: _noop,
    dutchiePayUnlinkError: _noop,
    loyaltySignup: _noop,
    gaGTMClickEvent: _noop,
    dispensaryClicked: _noop,
    dutchiePayKioskQRCodeModalViewed: _noop,
    viewedKioskCheckoutModal: _noop,
    loginNudge: _noop,
    loginGateShown: _noop,
    loginEntryPointClicked: _noop,
    dutchiePayCheckoutFlowAction: _noop,
    roundupForCharityViewed: _noop,
    roundupForCharityToggled: _noop,
    walletPass: _noop,
    dutchiePayLinkToPayInitiated: _noop,
    dutchiePayLinkToPayIneligible: _noop,
    dutchiePayEcommModalPaymentSwitchViewed: _noop,
    // Population/exposure is automatic from the flag evaluation itself (see
    // NewPbbCtaExperience) -- no separate viewed metric needed.
    dutchiePayEcommModalPaymentSwitchClicked: () => {
      client.trackMetric('fintech.order-confirmation.switch-payment-click');
    },
    dutchiePayEcommModalPaymentSwitchOutcome: _noop,
    dutchiePayLoyaltyCtaClicked: _noop,
    productRatingViewed: _noop,
    productReviewsViewed: _noop,
    productReviewAiSummaryViewed: _noop,
    productReviewStatusChecked: _noop,
    productReviewCtaClicked: _noop,
    productReviewSubmitted: _noop,
    instoreEnrollmentStepViewed: _noop,
    instoreEnrollmentStepCompleted: _noop,
    instoreEnrollmentStepSkipped: _noop,
    medCardSignupCompleted: _noop,
    medCardSignupFailed: _noop,
    loyaltyEnrollmentAction: _noop,
    onboardingModalStep: _noop,
    onboardingFlowStarted: _noop,
    conciergeEntryPointClicked: _noop,
    conciergeImpression: _noop,
    conciergeAction: _noop,
    conciergeMessageSent: _noop,
    conciergeExited: _noop,
    conciergeBundleAdded: _noop,
    conciergeAccessGate: _noop,
    conciergeConversationRefreshed: _noop,
    conciergeMessageStopped: _noop,
    conciergeError: _noop,
    conciergeIdleWarning: _noop,
  };
};
